/**
 * Admin Service
 * 用户管理 API 服务
 */
import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import { normalizeSearchText } from '../utils/searchText'

// ========== 类型定义 ==========

export interface AdminUser {
    id: number
    username: string
    display_name?: string | null
    role: string
    is_active: boolean
    is_superuser: boolean
    is_verified: boolean
    created_at: string
    created_by?: number | null
    region_id?: number | null
    account_type?: string
    home_project_id?: number | null
    api_key?: string | null
    has_api_credentials?: boolean
    api_secret_last4?: string | null
    api_credentials_updated_at?: string | null
}

export interface PaginatedUsers {
    users: AdminUser[]
    total: number
}

interface ScopedAdminUserResponse {
    id: number
    username: string
    display_name?: string | null
    role_name?: string | null
    is_active: boolean
    is_superuser: boolean
    created_at: string
    created_by?: number | null
    region_id?: number | null
    account_type?: string
    home_project_id?: number | null
    api_key?: string | null
    has_api_credentials?: boolean
    api_secret_last4?: string | null
    api_credentials_updated_at?: string | null
}

export interface ResetUserApiCredentialsResponse {
    user_id: number
    api_key: string
    api_secret: string
    api_secret_last4: string
    updated_at: string
}

type ScopedAdminUserListResponse = {
    users: ScopedAdminUserResponse[]
    total: number
}

export interface CreateUserRequest {
    username: string
    display_name?: string | null
    password: string
    role: string
    is_active?: boolean
    region_id?: number | null
    account_type?: string
    home_project_id?: number | null
}

export interface UpdateUserRequest {
    username?: string
    display_name?: string | null
    role?: string
    is_active?: boolean
    region_id?: number | null
    account_type?: string
    home_project_id?: number | null
}

export interface UpdateCurrentUserPasswordRequest {
    current_password: string
    new_password: string
}

export interface UpdateManagedUserPasswordRequest {
    new_password: string
}

type AdminUserPatchPayload = {
    username?: string
    display_name?: string | null
    region_id?: number | null
    is_active?: boolean
    role_name?: string
    account_type?: string
    home_project_id?: number | null
}

// ========== 用户管理 API ==========

const mapScopedAdminUser = (u: ScopedAdminUserResponse): AdminUser => ({
    id: u.id,
    username: u.username,
    display_name: u.display_name ?? null,
    role: u.role_name || '',
    is_active: u.is_active,
    is_superuser: u.is_superuser,
    is_verified: false,
    created_at: u.created_at,
    created_by: u.created_by ?? null,
    region_id: u.region_id,
    account_type: u.account_type || 'platform',
    home_project_id: u.home_project_id ?? null,
    api_key: u.api_key ?? null,
    has_api_credentials: u.has_api_credentials ?? false,
    api_secret_last4: u.api_secret_last4 ?? null,
    api_credentials_updated_at: u.api_credentials_updated_at ?? null,
})

/**
 * 获取所有用户列表（分页）
 */
export const getUsers = async (params?: {
    skip?: number
    limit?: number
    username?: string
}): Promise<PaginatedUsers> => {
    const page = Math.floor((params?.skip || 0) / (params?.limit || 10)) + 1
    const page_size = params?.limit || 10
    const response = await api.get<ScopedAdminUserListResponse>('/rbac/users', {
        params: {
            page,
            page_size,
            username: normalizeSearchText(params?.username) || undefined,
        }
    })
    return {
        users: response.data.users.map(mapScopedAdminUser),
        total: response.data.total
    }
}

/**
 * 获取单个用户详情
 */
export const getUser = async (userId: number): Promise<AdminUser> => {
    const response = await api.get<ScopedAdminUserResponse>(`/rbac/users/${userId}`)
    return mapScopedAdminUser(response.data)
}

/**
 * 创建用户
 */
export const createUser = async (
    data: CreateUserRequest,
    options?: MutationRequestOptions
): Promise<AdminUser> => {
    const response = await api.post<ScopedAdminUserResponse>('/rbac/users', {
        username: data.username,
        display_name: data.display_name,
        password: data.password,
        role_name: data.role,
        is_active: data.is_active,
        region_id: data.region_id,
        account_type: data.account_type,
        home_project_id: data.home_project_id,
    }, getMutationRequestConfig(options))

    return getUser(response.data.id)
}

/**
 * 更新用户
 * 分别调用不同的端点更新用户信息和角色
 */
export const updateUser = async (
    userId: number,
    data: UpdateUserRequest,
    options?: MutationRequestOptions
): Promise<AdminUser> => {
    const updatePayload: AdminUserPatchPayload = {}
    if (data.username !== undefined) updatePayload.username = data.username
    if (data.display_name !== undefined) updatePayload.display_name = data.display_name
    if (data.region_id !== undefined) updatePayload.region_id = data.region_id
    if (data.is_active !== undefined) updatePayload.is_active = data.is_active
    if (data.role !== undefined) updatePayload.role_name = data.role
    if (data.account_type !== undefined) updatePayload.account_type = data.account_type
    if (data.home_project_id !== undefined) updatePayload.home_project_id = data.home_project_id

    if (Object.keys(updatePayload).length === 0) {
        return getUser(userId)
    }

    const response = await api.patch<ScopedAdminUserResponse>(
        `/rbac/users/${userId}`,
        updatePayload,
        getMutationRequestConfig(options)
    )
    return mapScopedAdminUser(response.data)
}

/**
 * 删除用户
 */
export const deleteUser = async (
    userId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(`/rbac/users/${userId}`, getMutationRequestConfig(options))
}

export const updateCurrentUserPassword = async (
    data: UpdateCurrentUserPasswordRequest,
    options?: MutationRequestOptions
): Promise<{ message: string }> => {
    const response = await api.patch<{ message: string }>(
        '/users/me/password',
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const updateManagedUserPassword = async (
    userId: number,
    data: UpdateManagedUserPasswordRequest,
    options?: MutationRequestOptions
): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
        `/rbac/users/${userId}/password`,
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const resetUserApiCredentials = async (
    userId: number,
    options?: MutationRequestOptions
): Promise<ResetUserApiCredentialsResponse> => {
    const response = await api.post<ResetUserApiCredentialsResponse>(
        `/rbac/users/${userId}/api-credentials/reset`,
        undefined,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const revokeUserApiCredentials = async (
    userId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(
        `/rbac/users/${userId}/api-credentials`,
        getMutationRequestConfig(options)
    )
}

/**
 * 获取当前用户信息
 */
export const getCurrentUser = async (): Promise<AdminUser> => {
    const response = await api.get<AdminUser>('/users/me')
    return response.data
}

/**
 * 请求验证邮箱
 */
export const requestVerifyEmail = async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/request-verify-token', { email }, getMutationRequestConfig({
        preventDuplicate: false, // mutation-policy: allow-concurrent auth verification requests
    }))
    return response.data
}

/**
 * 验证用户邮箱
 */
export const verifyEmail = async (token: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/verify', { token }, getMutationRequestConfig({
        preventDuplicate: false, // mutation-policy: allow-concurrent auth verification requests
    }))
    return response.data
}

/**
 * 请求重置密码
 */
export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email }, getMutationRequestConfig({
        preventDuplicate: false, // mutation-policy: allow-concurrent password reset requests
    }))
    return response.data
}

/**
 * 重置密码
 */
export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', { token, password: newPassword }, getMutationRequestConfig({
        preventDuplicate: false, // mutation-policy: allow-concurrent password reset requests
    }))
    return response.data
}
